import { Box, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type {
	BilletsBetweenCobblesPickerCrew,
	BilletsBetweenCobblesPickerProductType,
	BilletsBetweenCobblesPickerReportType,
} from "../models/BilletsBetweenCobbles";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productTypeId: number | null;
	crewId: string | null;
	reportType: number;
	onProductTypeChange: (id: number | null) => void;
	onCrewChange: (id: string | null) => void;
	onReportTypeChange: (id: number) => void;
	productTypes: BilletsBetweenCobblesPickerProductType[];
	crews: BilletsBetweenCobblesPickerCrew[];
	reportTypes: BilletsBetweenCobblesPickerReportType[];
	onRefresh: () => void;
	onExportExcel: () => void;
	isLoading: boolean;
}

export const BilletsBetweenCobblesToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productTypeId,
	crewId,
	reportType,
	onProductTypeChange,
	onCrewChange,
	onReportTypeChange,
	productTypes,
	crews,
	reportTypes,
	onRefresh,
	onExportExcel,
	isLoading,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			<Box sx={{ width: 180 }}>
				<TextField
					select
					size="small"
					label="Product Type"
					fullWidth
					value={productTypeId == null ? "" : String(productTypeId)}
					onChange={(e) => {
						const v = e.target.value;
						onProductTypeChange(v === "" ? null : Number(v));
					}}
				>
					<MenuItem value="">All</MenuItem>
					{productTypes.map((pt) => (
						<MenuItem key={pt.Id} value={String(pt.Id)}>
							{pt.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 110 }}>
				<TextField
					select
					size="small"
					label="Shift"
					fullWidth
					value={crewId ?? ""}
					onChange={(e) => {
						const v = e.target.value;
						onCrewChange(v === "" ? null : v);
					}}
				>
					<MenuItem value="">All</MenuItem>
					{crews.map((c) => (
						<MenuItem key={c.Id} value={c.Id}>
							{c.Id}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 170 }}>
				<TextField
					select
					size="small"
					label="Report Type"
					fullWidth
					value={String(reportType)}
					onChange={(e) => onReportTypeChange(Number(e.target.value))}
				>
					{reportTypes.map((rt) => (
						<MenuItem key={rt.Id} value={String(rt.Id)}>
							{rt.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Tooltip title="Refresh">
				<span>
					<IconButton
						onClick={onRefresh}
						disabled={isLoading}
						size="small"
						sx={{
							bgcolor: "primary.main",
							color: "white",
							width: 28,
							height: 28,
							"&:hover": { bgcolor: "primary.dark" },
							"&.Mui-disabled": {
								bgcolor: "action.disabledBackground",
								color: "action.disabled",
							},
						}}
					>
						<RefreshIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</span>
			</Tooltip>

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
